import { TrendingUp, Minus, Eraser, Trash2, GitBranch, Network, Repeat } from "lucide-react";
import clsx from "clsx";
import { useDrawingsStore, chartKeyFor, type DrawingTool } from "@/store/drawings";

interface DrawingToolbarProps {
  symbol: string;
  exchange: string;
  timeframe: string;
}

const TOOLS: Array<{
  tool: DrawingTool;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  kbd: string;
}> = [
  { tool: "trend",           icon: TrendingUp, label: "Trend line",         kbd: "T" },
  { tool: "horizontal",      icon: Minus,      label: "Horizontal line",    kbd: "H" },
  { tool: "fib_retracement", icon: GitBranch,  label: "Fib retracement",    kbd: "F" },
  { tool: "fib_extension",   icon: Network,    label: "Fib extension",      kbd: "X" },
  { tool: "cycle",           icon: Repeat,     label: "Cycle lines",        kbd: "C" },
  { tool: "eraser",          icon: Eraser,     label: "Eraser",             kbd: "E" },
];

export function DrawingToolbar({ symbol, exchange, timeframe }: DrawingToolbarProps) {
  const { activeTool, setTool, drawings, clearForChart } = useDrawingsStore();
  const chartKey = chartKeyFor(symbol, exchange, timeframe);
  const drawingsOnChart = drawings.filter((d) => d.chartKey === chartKey).length;

  return (
    <div className="flex w-9 shrink-0 flex-col items-center gap-0.5 border-r border-border bg-bg-panel py-2">
      {TOOLS.map(({ tool, icon: Icon, label, kbd }) => {
        const active = activeTool === tool;
        return (
          <button
            key={tool}
            onClick={() => setTool(active ? null : tool)}
            title={`${label} (${kbd})`}
            aria-label={label}
            aria-pressed={active}
            className={clsx(
              "flex h-8 w-8 items-center justify-center rounded-md transition-colors",
              active
                ? "bg-accent text-white"
                : "text-text-secondary hover:bg-bg-elevated hover:text-text-primary"
            )}
          >
            <Icon className="h-4 w-4" />
          </button>
        );
      })}

      <div className="my-1 h-px w-5 bg-border" aria-hidden />

      {drawingsOnChart > 0 && (
        <button
          onClick={() => {
            if (confirm(`Clear all ${drawingsOnChart} drawings on this chart?`)) {
              clearForChart(chartKey);
            }
          }}
          title="Clear all drawings on this chart"
          aria-label="Clear drawings"
          className="flex h-8 w-8 items-center justify-center rounded-md text-text-secondary transition-colors hover:bg-bear/10 hover:text-bear"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      )}

      <div className="mt-auto">
        {drawingsOnChart > 0 && (
          <span
            className="rounded bg-bg-elevated px-1 py-0.5 text-[10px] font-mono text-text-muted"
            title={`${drawingsOnChart} drawings on this chart`}
          >
            {drawingsOnChart}
          </span>
        )}
      </div>
    </div>
  );
}
